import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { persuasionLabService, type PersuasionRun } from '@/services/persuasion-lab'
import type { AgentAttachment } from '@/services/agent/service'

type Props = {
    open: boolean
    onOpenChange: (open: boolean) => void
    onAdd: (attachments: AgentAttachment[]) => void
}

export default function FoundationPickerDialog({ open, onOpenChange, onAdd }: Props) {
    const [query, setQuery] = useState('')
    const [runs, setRuns] = useState<PersuasionRun[]>([])
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (!open) return
        let cancelled = false
        setIsLoading(true)
        persuasionLabService
            .listRuns({ rootsOnly: true })
            .then(res => {
                if (cancelled) return
                const list = (res.runs || [])
                    .filter(r => !!r._id)
                    // Foundations only: root runs, not argument/message builds.
                    .filter(r => !r.parentRunId && (!r.runType || r.runType === null))
                    // Hide archived from picker by default.
                    .filter(r => (r.status || '').toLowerCase() !== 'archived')
                setRuns(list)
            })
            .catch(() => {
                if (cancelled) return
                setRuns([])
            })
            .finally(() => {
                if (cancelled) return
                setIsLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [open])

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return runs
        return runs.filter(r => {
            const name = (r.name || '').toLowerCase()
            const targetName = String((r as any).targetName || '').toLowerCase()
            return name.includes(q) || targetName.includes(q)
        })
    }, [runs, query])

    const recent = useMemo(() => runs.slice(0, 8), [runs])

    const attach = (run: PersuasionRun) => {
        onAdd([
            {
                kind: 'foundation_run',
                id: run._id,
                name: run.name,
            },
        ])
        onOpenChange(false)
        setQuery('')
    }

    const shortId = (id: unknown, len = 6) => {
        const s = String(id || '').trim()
        if (!s) return ''
        return s.length > len ? s.slice(-len) : s
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='w-[calc(100vw-24px)] max-w-xl max-h-[calc(100dvh-24px)] flex flex-col gap-3'>
                <DialogHeader>
                    <DialogTitle>Attach a Foundation</DialogTitle>
                </DialogHeader>

                <div className='space-y-3'>
                    <Input
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder='Search foundations…'
                    />

                    <div className='max-h-80 overflow-y-auto rounded-lg border border-slate-200 bg-white'>
                        {isLoading ? (
                            <div className='p-3 text-sm text-slate-500'>Loading…</div>
                        ) : runs.length === 0 ? (
                            <div className='p-3 text-sm text-slate-500'>No foundations found.</div>
                        ) : (
                            <div className='divide-y divide-slate-100'>
                                {(query.trim() ? filtered : recent).slice(0, 50).map(run => (
                                    <button
                                        key={run._id}
                                        type='button'
                                        onClick={() => attach(run)}
                                        className='w-full min-h-[44px] px-3 py-2 text-left hover:bg-slate-50'
                                    >
                                        <div className='flex items-center gap-3'>
                                            <div className='flex-1 min-w-0'>
                                                <div className='text-sm font-medium text-slate-900 leading-snug line-clamp-2 break-words' title={run.name}>
                                                    {run.name}
                                                </div>
                                                <div className='truncate text-xs text-slate-500'>
                                                    {(run as any).targetName ? `Target: ${(run as any).targetName}` : 'Foundation'}
                                                </div>
                                                <div className='mt-0.5 text-[10px] font-mono text-slate-400'>ID: …{shortId(run._id)}</div>
                                            </div>
                                            <span className='shrink-0 whitespace-nowrap text-xs font-semibold text-primary-main'>Attach</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {!query.trim() && runs.length > recent.length ? (
                        <div className='text-[11px] text-slate-500'>
                            Showing recent foundations. Search to find older ones.
                        </div>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    )
}

